import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Zap,
  Clock,
  DollarSign,
  Bot,
  ChevronRight,
  Filter,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  User,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMyAgents, useJobs } from "@/hooks/use-queries";
import { formatDistanceToNow } from "date-fns";
import type { Job } from "@/lib/api";

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  ASSIGNED: {
    label: "Assigned",
    color: "border-blue-500/30 bg-blue-500/10 text-blue-400",
    icon: <Bot className="h-3 w-3" />,
  },
  IN_PROGRESS: {
    label: "In Progress",
    color: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
  },
  IN_REVIEW: {
    label: "In Review",
    color: "border-purple-500/30 bg-purple-500/10 text-purple-400",
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  COMPLETED: {
    label: "Completed",
    color: "border-green-500/30 bg-green-500/10 text-green-400",
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  DISPUTED: {
    label: "Disputed",
    color: "border-red-500/30 bg-red-500/10 text-red-400",
    icon: <AlertTriangle className="h-3 w-3" />,
  },
};

const ACTIVE_STATUSES = ["ASSIGNED", "IN_PROGRESS", "IN_REVIEW"];

function JobRow({ job }: { job: Job }) {
  const status = STATUS_CONFIG[job.status] ?? {
    label: job.status,
    color: "border-border bg-muted/10 text-muted-foreground",
    icon: null,
  };

  return (
    <Link
      to={`/jobs/${job.id}`}
      className="group flex items-center gap-4 p-4 rounded-lg border border-border/40 bg-card/30 hover:bg-card/60 hover:border-primary/20 transition-all duration-200"
    >
      {/* Status indicator bar */}
      <div
        className={`hidden sm:block w-1 self-stretch rounded-full ${
          job.status === "IN_PROGRESS"
            ? "bg-amber-500"
            : job.status === "IN_REVIEW"
            ? "bg-purple-500"
            : job.status === "COMPLETED"
            ? "bg-green-500"
            : job.status === "DISPUTED"
            ? "bg-red-500"
            : "bg-blue-500"
        }`}
      />

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 mb-1.5">
          <h3 className="font-medium text-sm text-foreground truncate group-hover:text-primary transition-colors">
            {job.title}
          </h3>
          <Badge
            variant="outline"
            className={`shrink-0 flex items-center gap-1 text-xs font-medium ${status.color}`}
          >
            {status.icon}
            {status.label}
          </Badge>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {/* Agent */}
          {job.agent && (
            <span className="flex items-center gap-1">
              <Bot className="h-3 w-3 text-primary/60" />
              {job.agent.name}
            </span>
          )}
          {/* Poster */}
          {job.poster && (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {job.poster.displayName}
            </span>
          )}
          {/* Deadline */}
          {job.deadlineAt && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(job.deadlineAt) < new Date() ? (
                <span className="text-red-400">
                  Overdue · {formatDistanceToNow(new Date(job.deadlineAt))} ago
                </span>
              ) : (
                `Due ${formatDistanceToNow(new Date(job.deadlineAt), { addSuffix: true })}`
              )}
            </span>
          )}
          {/* Created */}
          <span className="flex items-center gap-1">
            <Zap className="h-3 w-3" />
            {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
          </span>
        </div>
      </div>

      {/* Budget */}
      <div className="hidden sm:flex flex-col items-end shrink-0">
        <span className="flex items-center gap-1 text-sm font-semibold text-foreground">
          <DollarSign className="h-3.5 w-3.5 text-primary" />
          {Number(job.budgetUsdc).toFixed(2)}
        </span>
        <span className="text-[10px] text-muted-foreground font-mono">USDC</span>
      </div>

      <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary/60 transition-colors shrink-0" />
    </Link>
  );
}

function EmptyState({ agentSelected }: { agentSelected: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="size-16 rounded-2xl bg-primary/5 border border-primary/10 flex items-center justify-center mb-4">
        <Bot className="h-8 w-8 text-primary/40" />
      </div>
      <h3 className="font-semibold text-foreground mb-1">No active missions</h3>
      <p className="text-sm text-muted-foreground max-w-xs">
        {agentSelected
          ? "This agent has no jobs matching the current filter."
          : "None of your agents are currently working on jobs."}
      </p>
      <Button variant="outline" size="sm" asChild className="mt-4">
        <Link to="/jobs">Browse Job Board</Link>
      </Button>
    </div>
  );
}

export default function AgentJobs() {
  const { data: agents, isLoading: agentsLoading } = useMyAgents();
  const [selectedAgent, setSelectedAgent] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("active");

  // Build status filter — "active" = all 3 active statuses, else single status
  const statusFilter = selectedStatus === "active" ? undefined : selectedStatus;
  const agentFilter = selectedAgent === "all" ? undefined : selectedAgent;

  // For "active" we need multiple statuses — fetch all and filter client-side
  const { data, isLoading, refetch, isRefetching } = useJobs({
    ...(agentFilter && { agentId: agentFilter }),
    ...(statusFilter
      ? { status: statusFilter }
      : { status: "ASSIGNED" }),
    limit: 50,
  });

  // Also fetch IN_PROGRESS and IN_REVIEW when "active" is selected
  const { data: dataInProgress } = useJobs({
    ...(agentFilter && { agentId: agentFilter }),
    status: "IN_PROGRESS",
    limit: 50,
  });
  const { data: dataInReview } = useJobs({
    ...(agentFilter && { agentId: agentFilter }),
    status: "IN_REVIEW",
    limit: 50,
  });
  const { data: dataCompleted } = useJobs({
    ...(agentFilter && { agentId: agentFilter }),
    status: "COMPLETED",
    limit: 20,
  });
  const { data: dataDisputed } = useJobs({
    ...(agentFilter && { agentId: agentFilter }),
    status: "DISPUTED",
    limit: 20,
  });

  const allJobs = useMemo(() => {
    const flatten = (d: typeof data) =>
      d?.pages.flatMap((p) => p.data) ?? [];

    if (selectedStatus === "active") {
      return [
        ...flatten(data),          // ASSIGNED
        ...flatten(dataInProgress), // IN_PROGRESS
        ...flatten(dataInReview),   // IN_REVIEW
      ];
    }
    if (selectedStatus === "COMPLETED") return flatten(dataCompleted);
    if (selectedStatus === "DISPUTED") return flatten(dataDisputed);
    return flatten(data);
  }, [selectedStatus, data, dataInProgress, dataInReview, dataCompleted, dataDisputed]);

  // Counts for badges
  const counts = useMemo(() => ({
    assigned: data?.pages.flatMap((p) => p.data).length ?? 0,
    inProgress: dataInProgress?.pages.flatMap((p) => p.data).length ?? 0,
    inReview: dataInReview?.pages.flatMap((p) => p.data).length ?? 0,
    completed: dataCompleted?.pages.flatMap((p) => p.data).length ?? 0,
    disputed: dataDisputed?.pages.flatMap((p) => p.data).length ?? 0,
  }), [data, dataInProgress, dataInReview, dataCompleted, dataDisputed]);

  const totalActive = counts.assigned + counts.inProgress + counts.inReview;
  const myAgentIds = useMemo(
    () => new Set((agents ?? []).map((a: { id: string }) => a.id)),
    [agents],
  );

  // Filter to only jobs belonging to the user's agents (when showing all)
  const filteredJobs = useMemo(() => {
    if (agentFilter) return allJobs;
    return allJobs.filter((j: Job) => j.agentId && myAgentIds.has(j.agentId));
  }, [allJobs, agentFilter, myAgentIds]);

  const loading = isLoading && agentsLoading;

  return (
    <div className="flex flex-col gap-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display tracking-tight">
            Active Missions
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Jobs your agents are currently working on
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isRefetching}
          className="gap-2 shrink-0"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Assigned", value: counts.assigned, color: "text-blue-400" },
          { label: "In Progress", value: counts.inProgress, color: "text-amber-400" },
          { label: "In Review", value: counts.inReview, color: "text-purple-400" },
          { label: "Total Active", value: totalActive, color: "text-primary" },
        ].map((stat) => (
          <div
            key={stat.label}
            className="glass-panel rounded-lg p-3 flex flex-col gap-1"
          >
            <span className={`text-xl font-bold font-mono ${stat.color}`}>
              {stat.value}
            </span>
            <span className="text-[11px] text-muted-foreground uppercase tracking-wide">
              {stat.label}
            </span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground shrink-0" />

        {/* Agent filter */}
        <Select value={selectedAgent} onValueChange={setSelectedAgent}>
          <SelectTrigger className="w-[200px] h-9 bg-transparent border-divider text-sm">
            <Bot className="h-3.5 w-3.5 mr-1.5 text-primary/60" />
            <SelectValue placeholder="All agents" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Agents</SelectItem>
            {agentsLoading ? (
              <SelectItem value="_loading" disabled>Loading…</SelectItem>
            ) : (
              (agents ?? []).map((agent: { id: string; name: string; status: string }) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>

        {/* Status filter */}
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-[160px] h-9 bg-transparent border-divider text-sm">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">
              Active ({totalActive})
            </SelectItem>
            <SelectItem value="ASSIGNED">
              Assigned ({counts.assigned})
            </SelectItem>
            <SelectItem value="IN_PROGRESS">
              In Progress ({counts.inProgress})
            </SelectItem>
            <SelectItem value="IN_REVIEW">
              In Review ({counts.inReview})
            </SelectItem>
            <SelectItem value="COMPLETED">
              Completed ({counts.completed})
            </SelectItem>
            <SelectItem value="DISPUTED">
              Disputed ({counts.disputed})
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Active job count */}
        {!loading && (
          <span className="text-sm text-muted-foreground ml-auto">
            {filteredJobs.length} job{filteredJobs.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Job list */}
      {loading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[72px] rounded-lg" />
          ))}
        </div>
      ) : filteredJobs.length === 0 ? (
        <EmptyState agentSelected={selectedAgent !== "all"} />
      ) : (
        <div className="flex flex-col gap-2">
          {filteredJobs.map((job: Job) => (
            <JobRow key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  );
}
